import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSchemas } from "@/contexts/SchemaContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";
import { toast } from "sonner";
import FieldListEditor from "@/components/schema/FieldListEditor";
import ImportExport from "@/components/schema/ImportExport";
import { useReviewFlow } from "@/components/schema/ReviewFlow";
import { fmtMeta } from "@/lib/schema";

const H = "text-xs uppercase tracking-[0.18em] text-purple-700 mb-3";

// Admin > Structure > Patient structure
export default function StructurePatient() {
  const { reload } = useSchemas();
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [profiles, setProfiles] = useState("");
  const [zones, setZones] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/schema/patient");
      setDoc(data.data); setMeta(data.Meta);
      setProfiles(JSON.stringify(data.data.bodyProfiles, null, 2));
      setZones(JSON.stringify(data.data.reachZones, null, 2));
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const flow = useReviewFlow(() => { load(); reload(); });
  const edit = (patch) => { setDoc({ ...doc, ...patch }); setDirty(true); };

  const build = () => {
    try {
      return { ...doc, bodyProfiles: JSON.parse(profiles), reachZones: JSON.parse(zones) };
    } catch (e) { toast.error(`Body profiles / zones are not valid JSON: ${e.message}`); return null; }
  };
  const save = () => {
    const data = build();
    if (data) flow.start("Save patient structure", ({ dryRun, allowOlder }) =>
      api.put("/schema/patient", data, { params: { dry_run: dryRun, allow_older: allowOlder } }));
  };

  if (!doc) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="structure-patient">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-500" data-testid="structure-patient-meta">{fmtMeta(meta)}</div>
        <div className="flex flex-wrap gap-2">
          <ImportExport
            exportUrl="/schema/patient/export" filename="NeuroPatientSchema.json"
            importCall={({ dryRun, allowOlder }, json) => api.post("/schema/patient/import", json, { params: { dry_run: dryRun, allow_older: allowOlder } })}
            resetCall={({ dryRun }) => api.post("/schema/patient/reset", null, { params: { dry_run: dryRun } })}
            history={{ kind: "schema", recordId: "patient" }}
            onChanged={() => { load(); reload(); }}
          />
          <Button onClick={save} disabled={!dirty && profiles === JSON.stringify(doc.bodyProfiles, null, 2) && zones === JSON.stringify(doc.reachZones, null, 2)}
            className="btn-brand rounded-full" data-testid="structure-save"><Save className="h-4 w-4 mr-2" /> Review and save</Button>
        </div>
      </div>

      <div className="panel rounded-2xl p-5">
        <div className={H}>Groups and fields</div>
        <p className="text-xs text-slate-500 mb-3">Each group becomes a tab on the patient page. Fields marked with a lock are read by the Unity app.</p>
        <FieldListEditor fields={doc.patientInputs} onChange={(patientInputs) => edit({ patientInputs })} />
      </div>

      <div className="panel rounded-2xl p-5">
        <div className={H}>Arm arc fields (used for Left and Right)</div>
        <FieldListEditor fields={doc.arcFields} allowGroups={false} allowArc={false} onChange={(arcFields) => edit({ arcFields })} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel rounded-2xl p-5">
          <div className={H}>Body profile presets</div>
          <p className="text-xs text-slate-500 mb-2">Values copied into the patient when the Body field changes.</p>
          <Textarea rows={16} className="font-mono text-xs" value={profiles} onChange={(e) => { setProfiles(e.target.value); setDirty(true); }} data-testid="body-profiles" />
        </div>
        <div className="panel rounded-2xl p-5">
          <div className={H}>Reach zones</div>
          <p className="text-xs text-slate-500 mb-2">Cell order defines the order in the Zones string. Groups drive the quick buttons.</p>
          <Textarea rows={16} className="font-mono text-xs" value={zones} onChange={(e) => { setZones(e.target.value); setDirty(true); }} data-testid="reach-zones" />
        </div>
      </div>
      {flow.dialog}
    </div>
  );
}
